import React, { useEffect, useMemo, useState } from 'react';
import { activePb } from '../../lib/pb';
import { useData } from '../../data/DataProvider';
import { date, time } from '../../shared/formatters';
import { mapAuditEvent } from '../../data/mappers';
import SafeIcon from '../../common/SafeIcon';
import { Icons } from '../../shared/icons';
import EmptyState from '../../shared/components/EmptyState';

// ClientDetail "Activity" tab (tracking_tasks.md TRK-4.2) — a single merged,
// chronological dispute timeline per client:
//   audit_events (on demand — unbounded, never bulk-loaded into DataProvider)
//   + portal_visits + notification_log (already in useData)
//   + document_acknowledgements (already in useData)
//
// Evidence-wording contract (tracking_tasks.md §0.3): rows say
// "opened / downloaded / acknowledged" — never "received / read / proof".
// The CSV export carries the §0.3 disclaimer sentence in its header row.

const PAGE_SIZE = 50;

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'documents', label: 'Documents' },
  { key: 'invoices', label: 'Invoices' },
  { key: 'certificates', label: 'Certificates' },
  { key: 'logins', label: 'Logins' },
  { key: 'emails', label: 'Emails' },
  { key: 'acks', label: 'Acknowledgements' },
];

const DISCLAIMER = 'Activity log — indicative record of portal interactions. Not conclusive proof of receipt or comprehension.';

const EVENT_ICONS = {
  login: Icons.LogIn || Icons.Activity,
  view: Icons.Eye || Icons.FileText,
  download: Icons.Download || Icons.FileText,
  published: Icons.FilePlus || Icons.FileText,
  revised: Icons.Edit || Icons.FileText,
  visit: Icons.Activity,
  email: Icons.Mail || Icons.MessageCircle,
  ack: Icons.CheckCircle || Icons.FileText,
};

// Normalize every source into one row shape for the merged timeline.
function toRow(kind, item, { documentsById, invoicesById, certsById }) {
  if (kind === 'audit') {
    let entityLabel = item.file_name || item.entity_id || '';
    if (item.entity_type === 'document' && documentsById[item.entity_id]) {
      entityLabel = documentsById[item.entity_id].filename;
    } else if (item.entity_type === 'invoice' && invoicesById[item.entity_id]) {
      entityLabel = invoicesById[item.entity_id].number || entityLabel;
    } else if (item.entity_type === 'certificate' && certsById[item.entity_id]) {
      entityLabel = certsById[item.entity_id].certificate_number || certsById[item.entity_id].typeLabel || entityLabel;
    }
    const verb = item.event_type === 'published'
      ? 'Document published'
      : item.event_type === 'revised'
        ? 'Document replaced'
        : item.event_type === 'login'
          ? 'Signed in'
          : item.event_type === 'download'
            ? 'Downloaded'
            : 'Opened';
    return {
      id: `audit-${item.id}`,
      kind: 'audit',
      type: item.event_type,
      entityType: item.entity_type || '',
      verb,
      entityLabel,
      version: item.entity_version || '',
      actor: item.user_name || (item.metadata?.role ? `${item.metadata.role}` : ''),
      method: item.method || '',
      ip: item.ip || '',
      user_agent: item.user_agent || '',
      at: item.created_at,
    };
  }
  if (kind === 'visit') {
    return {
      id: `visit-${item.id}`,
      kind: 'visit',
      type: 'visit',
      verb: 'Viewed portal page',
      entityLabel: item.path,
      version: '',
      actor: '',
      method: '',
      ip: '',
      user_agent: '',
      at: item.visited_at,
    };
  }
  if (kind === 'email') {
    return {
      id: `notify-${item.id}`,
      kind: 'email',
      type: 'email',
      verb: `${item.status === 'failed' ? 'Send failed' : 'Sent'} (${item.channel})`,
      entityLabel: item.recipient,
      version: item.context || '',
      actor: '',
      method: '',
      ip: '',
      user_agent: '',
      at: item.created_at,
    };
  }
  // acknowledgement
  return {
    id: `ack-${item.id}`,
    kind: 'ack',
    type: 'ack',
    verb: 'Acknowledged',
    entityLabel: item.document_name || item.document_id,
    version: item.document_version || '',
    actor: '',
    method: '',
    ip: '',
    user_agent: '',
    at: item.acknowledged_at,
    ackText: item.acknowledged_text || '',
  };
}

export default function ActivityTab({ client }) {
  const { data } = useData();
  const [auditRows, setAuditRows] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [exporting, setExporting] = useState(false);

  const filterKey = `client_id="${client.id}"`;
  // audit_events listRule already scopes agency_admin/staff to the tenant —
  // no explicit tenant clause needed (and agency_admin must NOT be filtered).

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    activePb().collection('audit_events').getList(page, PAGE_SIZE, {
      filter: filterKey,
      sort: '-created',
      expand: 'user_id',
    })
      .then((result) => {
        if (!active) return;
        const mapped = result.items.map(mapAuditEvent);
        setAuditRows((current) => (page === 1 ? mapped : [...current, ...mapped]));
        setHasMore(result.totalPages > page);
      })
      .catch((err) => {
        if (active) setError(err?.message || 'Failed to load activity');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [client.id, page, filterKey]);

  const documentsById = useMemo(() => Object.fromEntries(data.documents.map((d) => [d.id, d])), [data.documents]);
  const invoicesById = useMemo(() => Object.fromEntries(data.invoices.map((i) => [i.id, i])), [data.invoices]);
  const certsById = useMemo(() => Object.fromEntries(data.certificates.map((c) => [c.id, c])), [data.certificates]);

  const allRows = useMemo(() => {
    const rows = [
      ...auditRows.map((item) => toRow('audit', item, { documentsById, invoicesById, certsById })),
      ...(data.portalVisits || [])
        .filter((v) => v.client_id === client.id)
        .map((v) => toRow('visit', v, {})),
      ...(data.notification_log || [])
        .filter((n) => auditRowsOrContextMatchesClient(n, client.id, data))
        .map((n) => toRow('email', n, {})),
      ...(data.documentAcks || [])
        .filter((a) => a.client_id === client.id)
        .map((a) => toRow('ack', a, {})),
    ];
    rows.sort((a, b) => new Date(b.at || 0) - new Date(a.at || 0));
    return rows;
  }, [auditRows, data.portalVisits, data.notification_log, data.documentAcks, data.documents, data.invoices, data.certificates, client.id, documentsById, invoicesById, certsById]);

  // notification_log rows have no client relation — match via context strings
  // that embed an id belonging to this client (quote_sent:<id> etc.). Best
  // effort: recipients matching the client email are included.
  function auditRowsOrContextMatchesClient(n, clientId, dataState) {
    const clientRec = dataState.clients.find((c) => c.id === clientId);
    if (clientRec?.email && n.recipient && n.recipient.toLowerCase() === clientRec.email.toLowerCase()) return true;
    if (clientRec?.phone && n.recipient && n.recipient.endsWith(clientRec.phone)) return true;
    return false;
  }

  const filteredRows = useMemo(() => {
    if (filter === 'all') return allRows;
    if (filter === 'documents') {
      return allRows.filter((r) => (r.kind === 'audit' && r.entityType === 'document') || r.kind === 'ack');
    }
    if (filter === 'invoices') {
      return allRows.filter((r) => r.kind === 'audit' && r.entityType === 'invoice');
    }
    if (filter === 'certificates') {
      return allRows.filter((r) => r.kind === 'audit' && r.entityType === 'certificate');
    }
    if (filter === 'logins') return allRows.filter((r) => r.type === 'login' || r.type === 'visit');
    if (filter === 'emails') return allRows.filter((r) => r.kind === 'email');
    if (filter === 'acks') return allRows.filter((r) => r.kind === 'ack');
    return allRows;
  }, [allRows, filter]);

  // TRK-4.2 export: getFullList with the same scope + disclaimer header.
  const exportCsv = async () => {
    setExporting(true);
    try {
      const pb = activePb();
      const records = await pb.collection('audit_events').getFullList({
        filter: filterKey,
        sort: '-created',
        expand: 'user_id',
      });
      const acks = (data.documentAcks || []).filter((a) => a.client_id === client.id);
      const csvEscape = (value) => {
        const s = String(value ?? '');
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const lines = [
        `# ${DISCLAIMER}`,
        'time,event,entity,version,actor,method,ip,user_agent,acknowledged_text',
        ...records.map((rec) => {
          const e = mapAuditEvent(rec);
          return [
            e.created_at, e.event_type, e.file_name || e.entity_id, e.entity_version,
            e.user_name || e.user_id, e.method || e.source, e.ip, e.user_agent, '',
          ].map(csvEscape).join(',');
        }),
        ...acks.map((a) => [
          a.acknowledged_at, 'acknowledgement', a.document_name, a.document_version,
          a.user_id, '', '', '', a.acknowledged_text,
        ].map(csvEscape).join(',')),
      ];
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      anchor.download = `activity-${(client.name || 'client').replace(/[^\w-]+/g, '_')}-${stamp}.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[activity] export failed', err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <section className="panel tab-panel">
      <div className="panel-head">
        <h3>Activity</h3>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <p className="field-hint" style={{ margin: 0 }}>Opened, downloaded &amp; acknowledged — never &ldquo;read&rdquo;.</p>
          <button className="secondary-button" onClick={exportCsv} disabled={exporting}>
            {exporting ? 'Exporting…' : 'Download activity report (CSV)'}
          </button>
        </div>
      </div>
      <div className="filter-chips" style={{ marginBottom: 14 }}>
        {FILTERS.map((f) => (
          <button key={f.key} className={`chip ${filter === f.key ? 'chip-active' : ''}`} onClick={() => setFilter(f.key)}>{f.label}</button>
        ))}
      </div>
      {error ? (
        <EmptyState title="Could not load activity" description={error} />
      ) : filteredRows.length === 0 && !loading ? (
        <EmptyState title="No activity yet" description="Logins, opened documents and acknowledgements appear here." />
      ) : (
        <div className="activity-list">
          {filteredRows.map((row) => (
            <div key={row.id} className="activity-row" title={row.user_agent || undefined}>
              <i><SafeIcon icon={EVENT_ICONS[row.type] || Icons.Activity} /></i>
              <p>
                <strong>{row.verb}</strong>{row.entityLabel ? ` — ${row.entityLabel}` : ''}
                {row.version ? <small> · {row.version}</small> : null}
                {row.method ? <small> · via {row.method}</small> : null}
                {row.ip ? <small> · {row.ip}</small> : null}
                {row.ackText ? <small> · &ldquo;{row.ackText}&rdquo;</small> : null}
              </p>
              <time>{date(row.at)}, {time(row.at)}</time>
            </div>
          ))}
        </div>
      )}
      {hasMore && (
        <div style={{ marginTop: 14, textAlign: 'center' }}>
          <button className="secondary-button" onClick={() => setPage((p) => p + 1)} disabled={loading}>
            {loading ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </section>
  );
}
